const SHOPLIFT_CONTROL_ID = '019f5c07-6b6f-71d4-a833-f20554d4d3d9';
const SHOPLIFT_VARIATION_ID = '019f5c07-6b7d-7045-8136-15caad916bd9';
const ACTIVE_CLASS = 'hide-header-on-scroll-test--variation';
const HIDDEN_CLASS = 'hide-header-on-scroll-test--header-hidden';
const RESIZING_CLASS = 'hide-header-on-scroll-test--resizing';
const PROMO_BAR_SELECTOR = [
  '#shopify-section-gld_promotion-bar',
  '#shopify-section-gld_promotion-bar-big',
  '.shopify-section-gld_promotion-bar--big',
].join(', ');
const HIDE_SCROLL_THRESHOLD = 8;
const SHOW_SCROLL_THRESHOLD = 8;
const RESIZE_SETTLE_DELAY = 150;
const TRANSITION_SETTLE_DELAY = 240;
const SHOPLIFT_TIMEOUT = 10000;

const waitForShoplift = () => {
  const startedAt = Date.now();

  return new Promise((resolve) => {
    const check = () => {
      if (typeof window.shoplift?.isHypothesisActive === 'function') {
        resolve(window.shoplift);
        return;
      }

      if (Date.now() - startedAt >= SHOPLIFT_TIMEOUT) {
        resolve(null);
        return;
      }

      window.setTimeout(check, 50);
    };

    check();
  });
};

const setupHideHeaderOnScrollTest = () => {
  const root = document.documentElement;
  const header = document.querySelector(
    '#shopify-section-header, #shopify-section-header-v2'
  );

  if (!header) return;

  let variationActive = false;
  let lastScrollY = Math.max(window.pageYOffset || 0, 0);
  let ticking = false;
  let resizing = false;
  let resizeTimeout = null;
  let transitionLocked = false;
  let transitionLockTimeout = null;

  const getPromoBars = () =>
    [...document.querySelectorAll(PROMO_BAR_SELECTOR)].filter(
      (promoBar, index, promoBars) => promoBars.indexOf(promoBar) === index
    );

  const isVisible = (element) => {
    const styles = window.getComputedStyle(element);

    return (
      styles.display !== 'none' &&
      styles.visibility !== 'hidden' &&
      element.getBoundingClientRect().height > 0
    );
  };

  const syncPromoBarHeight = () => {
    const height = getPromoBars().reduce((totalHeight, promoBar) => {
      if (!isVisible(promoBar)) return totalHeight;

      return totalHeight + promoBar.getBoundingClientRect().height;
    }, 0);

    root.style.setProperty('--hide-header-promo-bar-height', `${height}px`);
  };

  const syncHeaderHeight = () => {
    const headerHeight = Math.max(
      header.scrollHeight,
      header.getBoundingClientRect().height
    );

    root.style.setProperty(
      '--hide-header-test-header-height',
      `${headerHeight}px`
    );
  };

  const syncHeights = () => {
    syncPromoBarHeight();
    syncHeaderHeight();
  };

  const lockScrollDuringTransition = () => {
    transitionLocked = true;
    window.clearTimeout(transitionLockTimeout);

    transitionLockTimeout = window.setTimeout(() => {
      lastScrollY = Math.max(window.pageYOffset || 0, 0);
      transitionLocked = false;
    }, TRANSITION_SETTLE_DELAY);
  };

  const setHeaderHidden = (isHidden) => {
    const changed = root.classList.contains(HIDDEN_CLASS) !== isHidden;
    root.classList.toggle(HIDDEN_CLASS, isHidden);

    if (changed) lockScrollDuringTransition();
  };

  const showHeader = () => setHeaderHidden(false);

  const isSearchOverlayActive = () =>
    header.dataset.searchOverlayState === 'active';

  const syncSearchOverlayState = () => {
    if (isSearchOverlayActive()) {
      showHeader();
    }

    // Opening the overlay can lock the body at a synthetic scroll position,
    // while closing it restores the real one. Neither change represents user
    // scrolling, so keep it out of the hide-on-scroll direction calculation.
    lastScrollY = Math.max(window.pageYOffset || 0, 0);
    lockScrollDuringTransition();
  };

  const applyVariant = (isVariation) => {
    variationActive = isVariation === true;
    root.classList.toggle(ACTIVE_CLASS, variationActive);
    lastScrollY = Math.max(window.pageYOffset || 0, 0);
    syncHeights();

    if (!variationActive || lastScrollY <= 0) showHeader();
  };

  const updateOnScroll = () => {
    ticking = false;

    if (!variationActive) return;

    const currentScrollY = Math.max(window.pageYOffset || 0, 0);

    if (isSearchOverlayActive()) {
      showHeader();
      lastScrollY = currentScrollY;
      return;
    }

    if (resizing || transitionLocked) {
      lastScrollY = currentScrollY;
      return;
    }

    const delta = currentScrollY - lastScrollY;

    if (currentScrollY <= 0) {
      showHeader();
      lastScrollY = currentScrollY;
      return;
    }

    const threshold =
      delta > 0 ? HIDE_SCROLL_THRESHOLD : SHOW_SCROLL_THRESHOLD;

    if (Math.abs(delta) < threshold) return;

    setHeaderHidden(delta > 0);
    lastScrollY = currentScrollY;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateOnScroll);
  };

  const onResize = () => {
    resizing = true;
    root.classList.add(RESIZING_CLASS);
    syncHeights();
    window.clearTimeout(resizeTimeout);

    resizeTimeout = window.setTimeout(() => {
      syncHeights();
      lastScrollY = Math.max(window.pageYOffset || 0, 0);
      resizing = false;
      root.classList.remove(RESIZING_CLASS);
    }, RESIZE_SETTLE_DELAY);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);

  if (window.ResizeObserver) {
    const promoBarObserver = new ResizeObserver(syncPromoBarHeight);
    getPromoBars().forEach((promoBar) => promoBarObserver.observe(promoBar));
  }

  if (window.MutationObserver) {
    const promoBarMutationObserver = new MutationObserver(syncPromoBarHeight);
    const searchOverlayMutationObserver = new MutationObserver(
      syncSearchOverlayState
    );

    getPromoBars().forEach((promoBar) => {
      promoBarMutationObserver.observe(promoBar, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    });

    searchOverlayMutationObserver.observe(header, {
      attributes: true,
      attributeFilter: ['data-search-overlay-state'],
    });
  }

  applyVariant(false);

  waitForShoplift().then(async (shoplift) => {
    if (!shoplift) return;

    try {
      const isVariation = await shoplift.isHypothesisActive(
        SHOPLIFT_VARIATION_ID
      );

      if (!isVariation) {
        await shoplift.isHypothesisActive(SHOPLIFT_CONTROL_ID);
      }

      applyVariant(isVariation);
    } catch (error) {
      applyVariant(false);
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupHideHeaderOnScrollTest);
} else {
  setupHideHeaderOnScrollTest();
}
